import React from 'react';
import './ContactModule.css';

interface ContactModuleProps {
  heading: string;
  description: string;
  address: {
    label: string;
    lines: string[];
    mapsLink: string;
  };
  suites: {
    label: string;
    phones: { number: string; label?: string }[];
    email: string;
    whatsapp: {
      number: string;
      description: string;
    };
  };
  general: {
    label: string;
    phone: string;
    email: string;
  };
}

const ContactModule: React.FC<ContactModuleProps> = ({
  heading,
  description,
  address,
  suites,
  general,
}) => {
  return (
    <section className="contact-module">
      <div className="contact-main-wrapper">
        <div className="contact-text-wrapper">
          <p className="contact-heading">{heading}</p>
          <div className="contact-body-copy">
            <div className="contact-main-description">{description}</div>
          </div>
        </div>

        <span className="contact-separator"></span>

        {/* Map Section */}
        <div className="contact-map-section">
          <h3 className="map-heading">Find Us Here</h3>
          <div className="map-container">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3557.8936762891394!2d75.7864244!3d26.9466095!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x396db3c16991d11f%3A0xd3648878ab78f70f!2sHotel%20Baba%20Haveli!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="The Jaipur Hotels Location"
            ></iframe>
          </div>
        </div>

        <span className="contact-separator"></span>

        <div className="contact-desktop-view">
          <div className="contacts-grid">
            {/* Address */}
            <div className="contact-container">
              <div className="contact-wrapper">
                <div className="contact-label">{address.label}</div>
                <div className="contact-details">
                  {address.lines.map((line, index) => (
                    <p key={index}>{line}</p>
                  ))}
                  <p>
                    <a
                      href={address.mapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Open in Google Maps"
                    >
                      Open in Google Maps
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Suites Reservations */}
            <div className="contact-container">
              <div className="contact-wrapper">
                <div className="contact-label">{suites.label}</div>
                <div className="contact-details">
                  {suites.phones.map((phone, index) => (
                    <p key={index} className="contact-item">
                      <span className="contact-icon">📞</span>
                      <a href={`tel:${phone.number.replace(/\s/g, '')}`} className="contact-link">
                        {phone.number}
                      </a>
                      {phone.label && <span className="contact-label-text"> ({phone.label})</span>}
                    </p>
                  ))}
                  <p className="contact-item">
                    <span className="contact-icon">✉️</span>
                    <a href={`mailto:${suites.email}`} className="contact-link">{suites.email}</a>
                  </p>
                  <p>&nbsp;</p>
                  <p className="contact-section-title">ROOM RESERVATIONS</p>
                  <p>{suites.whatsapp.description}</p>
                </div>
                <div className="channel-container">
                  <div className="channel-wrapper">
                    <a
                      href={`https://wa.me/${suites.whatsapp.number.replace(/\s/g, '').replace(/\+/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="whatsapp-link"
                    >
                      <div className="whatsapp-icon">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12.3335 0.57373C5.90059 0.57373 0.666823 5.8075 0.666823 12.2404C0.666823 14.2491 1.18574 16.2243 2.16979 17.9672L0.685591 23.2629C0.636896 23.4368 0.68407 23.6235 0.809867 23.7529C0.906751 23.8528 1.03863 23.9071 1.17407 23.9071C1.21465 23.9071 1.25574 23.902 1.29581 23.8924L6.82277 22.5233C8.51037 23.4292 10.411 23.9071 12.3335 23.9071C18.7664 23.9071 24.0002 18.6733 24.0002 12.2404C24.0002 5.8075 18.7664 0.57373 12.3335 0.57373ZM18.2023 16.3572C17.9528 17.0481 16.7557 17.6786 16.1804 17.7633C15.6641 17.8389 15.0107 17.8713 14.2935 17.6461C13.8588 17.5092 13.3008 17.3276 12.5861 17.0227C9.58168 15.7414 7.61965 12.7542 7.4695 12.5569C7.31987 12.3596 6.24653 10.9535 6.24653 9.49822C6.24653 8.04293 7.02008 7.32721 7.29501 7.03098C7.56994 6.73474 7.89407 6.66069 8.09392 6.66069C8.29378 6.66069 8.49313 6.66322 8.66813 6.67134C8.85226 6.68047 9.09929 6.60185 9.34226 7.17909C9.59182 7.77156 10.1909 9.22685 10.2649 9.37547C10.34 9.52359 10.3897 9.69656 10.2903 9.89388C10.1909 10.0912 10.1412 10.2145 9.99103 10.3874C9.84088 10.5604 9.67653 10.7729 9.54161 10.9058C9.39146 11.0534 9.23574 11.2132 9.41023 11.5095C9.58472 11.8057 10.1858 12.774 11.0765 13.5582C12.2204 14.5656 13.1857 14.8781 13.4849 15.0262C13.7842 15.1743 13.9592 15.1495 14.1337 14.9521C14.3082 14.7543 14.8824 14.0883 15.0818 13.7926C15.2811 13.4968 15.481 13.5455 15.7559 13.6445C16.0308 13.7429 17.5033 14.4581 17.8026 14.6062C18.1019 14.7543 18.3018 14.8284 18.3768 14.9516C18.4519 15.0744 18.4519 15.6668 18.2023 16.3572Z"
                            fill="black"
                          />
                        </svg>
                      </div>
                      <span>Chat on WhatsApp</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* General Enquiries */}
            <div className="contact-container">
              <div className="contact-wrapper">
                <div className="contact-label">{general.label}</div>
                <div className="contact-details">
                  <p className="contact-item">
                    <span className="contact-icon">📞</span>
                    <a href={`tel:${general.phone.replace(/\s/g, '')}`} className="contact-link">
                      {general.phone}
                    </a>
                  </p>
                  <p className="contact-item">
                    <span className="contact-icon">✉️</span>
                    <a href={`mailto:${general.email}`} className="contact-link">{general.email}</a>
                  </p>
                  <p className="contact-service-note">24/7 Front Desk Service Available</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactModule;
